import { useState } from 'react';
import { api } from '../lib/api';
import { MoodLevel, EnergyLevel, DependentCheckIn } from '@kinapp/shared';

// ─── Mood config ────────────────────────────────────────────────────────────

const MOOD_OPTIONS: { value: MoodLevel; label: string; pill: string; dot: string }[] = [
  { value: MoodLevel.GREAT, label: 'Great',  pill: 'bg-green-100 text-green-800 ring-green-300',    dot: 'bg-green-400' },
  { value: MoodLevel.GOOD,  label: 'Good',   pill: 'bg-emerald-100 text-emerald-800 ring-emerald-300', dot: 'bg-emerald-300' },
  { value: MoodLevel.OKAY,  label: 'Okay',   pill: 'bg-amber-100 text-amber-800 ring-amber-300',    dot: 'bg-amber-400' },
  { value: MoodLevel.LOW,   label: 'Low',    pill: 'bg-orange-100 text-orange-800 ring-orange-300', dot: 'bg-orange-400' },
  { value: MoodLevel.POOR,  label: 'Poor',   pill: 'bg-red-100 text-red-800 ring-red-300',          dot: 'bg-red-400' },
];

const ENERGY_OPTIONS: { value: EnergyLevel; label: string }[] = [
  { value: EnergyLevel.HIGH,      label: 'High' },
  { value: EnergyLevel.NORMAL,    label: 'Normal' },
  { value: EnergyLevel.LOW,       label: 'Low' },
  { value: EnergyLevel.VERY_LOW,  label: 'Very low' },
];

function moodConfig(mood: MoodLevel) {
  return MOOD_OPTIONS.find((m) => m.value === mood) ?? MOOD_OPTIONS[2];
}

// ─── 7-day trend ────────────────────────────────────────────────────────────

function TrendDots({
  dependentId,
  recentCheckIns,
}: {
  dependentId: string;
  recentCheckIns: DependentCheckIn[];
}) {
  // Build last 7 calendar days (today = rightmost)
  const days: Date[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    days.push(d);
  }

  const depCheckIns = recentCheckIns.filter((c) => c.dependentId === dependentId);

  return (
    <div className="flex items-center gap-1.5 mt-3">
      <span className="text-xs text-gray-400 mr-1">7 days</span>
      {days.map((day, i) => {
        const dateStr = day.toISOString().split('T')[0];
        const checkIn = depCheckIns.find((c) => c.checkInDate.split('T')[0] === dateStr);
        const config = checkIn ? moodConfig(checkIn.mood) : null;
        const dayLabel = day.toLocaleDateString('en-GB', { weekday: 'short' })[0];
        return (
          <div key={i} className="flex flex-col items-center gap-0.5">
            <div
              title={checkIn ? `${checkIn.mood.charAt(0) + checkIn.mood.slice(1).toLowerCase()}` : 'No check-in'}
              className={`w-4 h-4 rounded-full ${config ? config.dot : 'bg-gray-200'}`}
            />
            <span className="text-[9px] text-gray-400">{dayLabel}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── CheckInCard ─────────────────────────────────────────────────────────────

interface CheckInCardProps {
  dependentName: string;
  dependentId: string;
  familyId: string;
  checkIn: DependentCheckIn | null;
  recentCheckIns: DependentCheckIn[];
  onSaved: () => void;
}

export function CheckInCard({
  dependentName,
  dependentId,
  familyId,
  checkIn,
  recentCheckIns,
  onSaved,
}: CheckInCardProps) {
  const [editing, setEditing] = useState(!checkIn);
  const [mood, setMood] = useState<MoodLevel | null>(checkIn?.mood ?? null);
  const [energy, setEnergy] = useState<EnergyLevel | null>(checkIn?.energy ?? null);
  const [notes, setNotes] = useState(checkIn?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!mood || !energy) return;
    setSaving(true);
    setError('');
    try {
      await api.createCheckIn(familyId, dependentId, {
        mood,
        energy,
        notes: notes.trim() || undefined,
      });
      setEditing(false);
      onSaved();
    } catch {
      setError('Failed to save check-in');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  // ── Display mode ──────────────────────────────────────────────────────────
  if (!editing && checkIn) {
    const config = moodConfig(checkIn.mood);
    const energyLabel = ENERGY_OPTIONS.find((e) => e.value === checkIn.energy)?.label ?? checkIn.energy;

    return (
      <div className="card">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-sm font-semibold text-gray-900">
            How is {dependentName} today?
          </h3>
          <button
            onClick={() => {
              setMood(checkIn.mood);
              setEnergy(checkIn.energy);
              setNotes(checkIn.notes ?? '');
              setEditing(true);
            }}
            className="text-xs text-primary-600 hover:text-primary-700 shrink-0"
          >
            Update
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset ${config.pill}`}>
            {config.label}
          </span>
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            Energy: {energyLabel}
          </span>
        </div>

        {checkIn.notes && (
          <p className="text-sm text-gray-600 mt-2">{checkIn.notes}</p>
        )}

        <p className="text-xs text-gray-400 mt-2">
          Logged by {checkIn.recordedBy?.name ?? 'someone'} · {formatTime(checkIn.updatedAt)}
        </p>

        <TrendDots dependentId={dependentId} recentCheckIns={recentCheckIns} />
      </div>
    );
  }

  // ── Edit / log mode ───────────────────────────────────────────────────────
  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        How is {dependentName} today?
      </h3>

      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

      {/* Mood selector */}
      <div className="mb-3">
        <p className="text-xs text-gray-500 mb-2">Mood</p>
        <div className="flex gap-1.5 flex-wrap">
          {MOOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setMood(opt.value)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                mood === opt.value
                  ? `${opt.pill} ring-1 ring-inset border-transparent font-medium`
                  : 'border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Energy selector */}
      <div className="mb-3">
        <p className="text-xs text-gray-500 mb-2">Energy</p>
        <div className="flex gap-1.5 flex-wrap">
          {ENERGY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setEnergy(opt.value)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                energy === opt.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                  : 'border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Any notes? (optional)"
        rows={2}
        className="input w-full resize-none text-sm mb-3"
      />

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={!mood || !energy || saving}
          className="btn btn-primary flex-1 text-sm py-1.5"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {checkIn && (
          <button
            onClick={() => setEditing(false)}
            className="btn flex-1 text-sm py-1.5"
          >
            Cancel
          </button>
        )}
      </div>

      {checkIn && (
        <TrendDots dependentId={dependentId} recentCheckIns={recentCheckIns} />
      )}
    </div>
  );
}
